import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Button, Chip, Stack, TextField, Tooltip } from '@mui/material'
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid'
import type { TipologiaImpianto } from '@praticheenergia/domain'
import { ETICHETTA_TIPOLOGIA } from '../home/ripartizione'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { caricaElenco } from './impianti.api'
import { COLONNE_IMPIANTI } from './colonne'
import { FILTRI_ELENCO } from './filtri'
import { DialogoCreazioneImpianto } from './DialogoCreazioneImpianto'

export function ElencoImpianti() {
  // Il filtro vive nell'indirizzo: cosi' i collegamenti del cruscotto e delle
  // scorciatoie arrivano all'elenco gia' filtrato, e la pagina si puo' condividere.
  const [parametri, setParametri] = useSearchParams()
  const filtro = parametri.get('filtro')
  const azienda = parametri.get('azienda')
  const aziendaId = azienda === null ? null : Number(azienda)
  const tipo = parametri.get('tipo') ?? ''
  const operatore = parametri.get('operatore') ?? ''
  // Un parametro alla volta, lasciando gli altri: cambiare lo stato non deve
  // perdere tipo, operatore o azienda gia' scelti.
  const imposta = (nome: string, valore: string | null) =>
    setParametri(
      (p) => {
        if (valore) p.set(nome, valore)
        else p.delete(nome)
        return p
      },
      { replace: true },
    )
  const setFiltro = (chiave: string) => imposta('filtro', chiave)
  const [dialogoAperto, setDialogoAperto] = useState(false)
  const queryClient = useQueryClient()
  const { data: righe = [], isLoading, error } = useQuery({
    queryKey: ['impianti', filtro, aziendaId],
    queryFn: () => caricaElenco(filtro, aziendaId),
  })
  // Tipo e operatore si filtrano qui e non nella query: gli impianti di un
  // partner sono centinaia, e le opzioni dell'operatore vengono dalle righe.
  // ponytail: se l'elenco arriva alle migliaia, passarli a caricaElenco come eq.
  const operatori = useMemo(
    () => [...new Set(righe.map((r) => r.operatore).filter((o): o is string => o !== null))].sort(),
    [righe],
  )
  const visibili = useMemo(
    () => righe.filter((r) => (!tipo || r.tipologia === tipo) && (!operatore || r.operatore === operatore)),
    [righe, tipo, operatore],
  )

  return (
    <Box>
      {error && (
        <p role="alert">{error instanceof Error ? error.message : 'Errore nel caricamento degli impianti'}</p>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {FILTRI_ELENCO.map((f) =>
            f.attivoDaSP5 ? (
              <Tooltip key={f.chiave} title="Disponibile con il prelievo automatico (SP5)">
                <span>
                  <Chip label={f.etichetta} clickable disabled />
                </span>
              </Tooltip>
            ) : (
              <Chip
                key={f.chiave}
                label={f.etichetta}
                color={filtro === f.chiave ? 'primary' : 'default'}
                onClick={() => setFiltro(f.chiave)}
              />
            ),
          )}
        </Stack>
        {aziendaId !== null && (
          <Chip
            label="Tutte le aziende"
            onDelete={() => imposta('azienda', null)}
            onClick={() => imposta('azienda', null)}
            color="primary"
          />
        )}
        <Button variant="contained" onClick={() => setDialogoAperto(true)}>
          Aggiungi Impianto
        </Button>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Tipo di impianto"
          sx={{ minWidth: 240 }}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          value={tipo}
          onChange={(e) => imposta('tipo', e.target.value)}
        >
          <option value="">Tutti</option>
          {(Object.entries(ETICHETTA_TIPOLOGIA) as [TipologiaImpianto, string][]).map(([chiave, etichetta]) => (
            <option key={chiave} value={chiave}>
              {etichetta}
            </option>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Operatore"
          sx={{ minWidth: 180 }}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          value={operatore}
          onChange={(e) => imposta('operatore', e.target.value)}
        >
          <option value="">Tutti</option>
          {operatori.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </TextField>
      </Stack>
      <DataGrid
        rows={visibili}
        columns={COLONNE_IMPIANTI}
        loading={isLoading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: BarraRicerca }}
      />
      <DialogoCreazioneImpianto
        aperto={dialogoAperto}
        onChiudi={() => {
          setDialogoAperto(false)
          void queryClient.invalidateQueries({ queryKey: ['impianti'] })
        }}
      />
    </Box>
  )
}

/** Ricerca libera: la quick filter della griglia cerca su tutte le colonne. */
function BarraRicerca() {
  return (
    <GridToolbarContainer>
      <GridToolbarQuickFilter placeholder="Cerca" />
    </GridToolbarContainer>
  )
}
